import { CurrentTapType, Dispute } from "pages/admin/Disputes/Disputes"
import React, { useMemo } from "react"
import {
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap"
import { useTranslation } from "react-i18next"

interface DisputesCardProps {
  dispute: Dispute
  resolveHandler: (dispute: Dispute) => void
  rejectHandler: (dispute: Dispute) => void
  viewDetailsHandler: (dispute: Dispute) => void
  forwardToAdminHandler: (dispute: Dispute) => void
}

const DisputesCard: React.FC<DisputesCardProps> = ({
  dispute,
  forwardToAdminHandler,
  rejectHandler,
  resolveHandler,
  viewDetailsHandler,
}) => {
  const { t } = useTranslation()

  const modifiedDescription = useMemo(() => {
    if (dispute.description.length > 196) {
      return dispute.description.slice(0, 196) + "..."
    }
    return dispute.description
  }, [dispute])

  return (
    <Col xxl="6" xl="6" md="6" sm="12">
      <Card className="text-center">
        <CardBody className="p-0">
          <div className="card-body p-2 px-3">
            <div className="d-flex justify-content-between align-items-start border p-2 rounded">
              <div className="d-flex justify-content-between align-items-start w-100">
                <div className="d-flex flex-column justify-content-start align-items-start gap-1">
                  <div className="d-flex gap-5 justify-content-start align-items-center">
                    <p className="mb-0 fw-semibold">{dispute.title}</p>
                    <p className="mb-0 font-size-12 fw-semibold">
                      {dispute.id}
                    </p>
                  </div>
                  <p className="mb-0 font-size-12 text-muted text-capitalize">
                    {t("common_createdAt")} {dispute.createdDate}
                  </p>
                  <div className="d-flex justify-content-start align-items-center gap-2">
                    <p className="mb-0 font-size-12 text-muted text-capitalize">
                      {t("common_createdBy")}
                    </p>
                    <img
                      src="https://placehold.co/16x16/png"
                      alt="avatar"
                      className="rounded-circle"
                      style={{
                        width: "16px",
                        height: "16px",
                      }}
                    />
                    <p className="mb-0 font-size-12 fw-semibold text-capitalize">
                      {dispute.createdBy}
                    </p>
                  </div>
                </div>
                <div className="d-flex justify-content-start align-items-center gap-1">
                  <span
                    className={`badge ${
                      dispute.status === CurrentTapType.solved
                        ? "text-success bg-success-30"
                        : dispute.status === CurrentTapType.pending
                        ? "text-cyan bg-cyan-30"
                        : dispute.status === CurrentTapType.rejected
                          ? "text-danger bg-danger-30"
                          : "text-primary bg-primary-30"
                    } px-2 py-1 font-size-10`}
                  >
                    {t(dispute.status)}
                  </span>
                  <UncontrolledDropdown>
                    <DropdownToggle tag="a" className="card-drop">
                      <i className="mdi mdi-dots-vertical font-size-18"></i>
                    </DropdownToggle>

                    <DropdownMenu className="dropdown-menu-end">
                      <DropdownItem onClick={() => viewDetailsHandler(dispute)}>
                        <span className="">{t("common_viewDetails")}</span>
                      </DropdownItem>
                      <DropdownItem onClick={() => resolveHandler(dispute)}>
                        <span className="">{t("common_resolved")}</span>
                      </DropdownItem>
                      <DropdownItem onClick={() => rejectHandler(dispute)}>
                        <span className="">{t("common_rejected")}</span>
                      </DropdownItem>
                      <DropdownItem
                        onClick={() => forwardToAdminHandler(dispute)}
                      >
                        <span className="">{t("common_forward")}</span>
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </div>
              </div>
              <i className="bi bi-three-dots-vertical"></i>
            </div>

            <div className="mt-3 px-2">
              <p className="mb-0 text-start font-size-14">
                {modifiedDescription}
              </p>
            </div>

            <div className="mt-3 px-2 d-flex justify-content-start gap-1 items-center mb-3">
              {dispute.attachments.map(attachment => (
                <div
                  key={attachment}
                  className="badge fw-semibold bg-secondary d-flex gap-1 items-center justify-content-start"
                >
                  <span className="px-2 py-1 font-size-14 ">{attachment}</span>
                  <i className="mdi mdi-close font-size-14 align-self-center cursor-pointer"></i>
                </div>
              ))}
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  )
}

interface DisputesGridProps {
  disputes: Dispute[]
  resolveHandler: (dispute: Dispute) => void
  rejectHandler: (dispute: Dispute) => void
  viewDetailsHandler: (dispute: Dispute) => void
  forwardToAdminHandler: (dispute: Dispute) => void
}
export const DisputesGrid: React.FC<DisputesGridProps> = ({
  disputes,
  forwardToAdminHandler,
  rejectHandler,
  resolveHandler,
  viewDetailsHandler,
}) => {
  return (
    <Row>
      {disputes.map(dispute => (
        <DisputesCard
          key={dispute.id}
          dispute={dispute}
          viewDetailsHandler={viewDetailsHandler}
          forwardToAdminHandler={forwardToAdminHandler}
          rejectHandler={rejectHandler}
          resolveHandler={resolveHandler}
        />
      ))}
    </Row>
  )
}
